import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Table,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  message,
  Divider,
  Tooltip,
  Descriptions,
  Dropdown,
  Tabs
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
  PrinterOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SendOutlined,
  FileTextOutlined,
  CopyOutlined,
  MoreOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { MenuProps } from 'antd';
import dayjs from 'dayjs';
import quotationService, {
  Quotation,
  QuotationItem,
  QuotationOption,
  QuotationStats,
  CreateQuotationInput,
  UpdateQuotationInput,
  QuotationFilters
} from '@/services/quotationService';
import quotationItemTemplateService, {
  QuotationItemTemplate
} from '@/services/quotationItemTemplateService';
import apiService from '@/services/apiService';
import { useDraggableRows } from '@/components/table/useDraggableRows';
import { companySwitcherService } from '@/services/companySwitcherService';
import QuotationItemTemplateList from '../QuotationItemTemplateManagement/QuotationItemTemplateList';
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

interface Customer {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
}

const QuotationManagement: React.FC = () => {
    const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('quotations');
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [itemTemplates, setItemTemplates] = useState<QuotationItemTemplate[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  // Staff who can be named as the salesperson on a quote.
  // Free-text filter over quotation no., customer and salesperson.
  const [searchText, setSearchText] = useState('');
  // Client feedback #19: filter the list by status, salesperson and date, each
  // taking several values at once.
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [salespersonFilter, setSalespersonFilter] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState<Quotation | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');
  const [quotationItems, setQuotationItems] = useState<Partial<QuotationItem>[]>([]);
  const [quotationOptions, setQuotationOptions] = useState<Partial<QuotationOption>[]>([]);
  const [stats, setStats] = useState<QuotationStats>({
    totalQuotations: 0,
    draftQuotations: 0,
    sentQuotations: 0,
    acceptedQuotations: 0,
    rejectedQuotations: 0,
    expiredQuotations: 0,
    totalAmount: 0,
    acceptedAmount: 0,
    pendingAmount: 0,
    conversionRate: 0
  });
  const [filters, setFilters] = useState<QuotationFilters>({});
  const [activeCompanyCode, setActiveCompanyCode] = useState<string>('SPL');
  const [accountingSettings, setAccountingSettings] = useState<any>(null);

  // User-extensible Unit + Payment Period dropdowns, persisted on accounting_settings.
  const DEFAULT_UNIT_OPTIONS = ['unit(s)', 'Rack(s)', 'Store', 'Month(s)', 'Order(s)', 'Hour(s)'];
  const DEFAULT_PAYMENT_PERIOD_OPTIONS = ['7 Days', '15th of the Following Month', '30 Days', 'Net 30', 'Net 60', 'Due on Receipt', 'COD', '50% Deposit'];
  const DEFAULT_SUBCATEGORY_OPTIONS = ['Extra Storage', 'Value-added Service'];
  const [unitOptions, setUnitOptions] = useState<string[]>(DEFAULT_UNIT_OPTIONS);
  const [paymentPeriodOptions, setPaymentPeriodOptions] = useState<string[]>(DEFAULT_PAYMENT_PERIOD_OPTIONS);
  const [subcategoryOptions, setSubcategoryOptions] = useState<string[]>(DEFAULT_SUBCATEGORY_OPTIONS);
  const [newUnitInput, setNewUnitInput] = useState<string>('');
  const [newPaymentPeriodInput, setNewPaymentPeriodInput] = useState<string>('');
  const [newSubcategoryInput, setNewSubcategoryInput] = useState<string>('');
  // Salespeople start empty on purpose (client feedback #17): the list is
  // whatever this company types, not a set of names we guessed for them.
  const [salespersonOptions, setSalespersonOptions] = useState<string[]>([]);
  const [newSalespersonInput, setNewSalespersonInput] = useState<string>('');

  useEffect(() => {
    const merge = (defaults: string[], extras: string[] | undefined | null) => {
      const seen = new Set(defaults);
      const extra = (extras || []).filter(v => v && !seen.has(v));
      return [...defaults, ...extra];
    };
    setUnitOptions(merge(DEFAULT_UNIT_OPTIONS, accountingSettings?.unitOptions));
    setPaymentPeriodOptions(merge(DEFAULT_PAYMENT_PERIOD_OPTIONS, accountingSettings?.paymentPeriodOptions));
    setSubcategoryOptions(merge(DEFAULT_SUBCATEGORY_OPTIONS, accountingSettings?.subcategoryOptions));
    setSalespersonOptions(merge([], accountingSettings?.salespersonOptions));
  }, [accountingSettings]);

  // Drag-and-drop row reordering for the quotation items + options tables.
  // sortOrder gets reassigned on save (the create/update payload mappers index
  // by position), so we just need to keep the array in user-visible order.
  const { onRow: itemsRowDrag, dragHandleColumn: itemsDragHandleColumn } =
    useDraggableRows(quotationItems, setQuotationItems);
  const { onRow: optionsRowDrag, dragHandleColumn: optionsDragHandleColumn } =
    useDraggableRows(quotationOptions, setQuotationOptions);

  const persistCompanyOption = async (kind: 'unitOptions' | 'paymentPeriodOptions' | 'subcategoryOptions' | 'salespersonOptions', value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    const current: string[] = (accountingSettings?.[kind] as string[]) || [];
    if (current.includes(trimmed)) return;
    const updated = [...current, trimmed];
    try {
      await apiService.put('/accounting/settings', { [kind]: updated });
      setAccountingSettings({ ...(accountingSettings || {}), [kind]: updated });
    } catch (err) {
      console.error(`Failed to persist ${kind}:`, err);
    }
  };

  // Counterpart to persistCompanyOption — remove a user-added option from
  // both local state and company settings. Defaults are not removable
  // (the × button is hidden for them by renderOptionContent).
  const removeCompanyOption = async (
    kind: 'unitOptions' | 'paymentPeriodOptions' | 'subcategoryOptions' | 'salespersonOptions',
    value: string,
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setter(prev => prev.filter(v => v !== trimmed));
    const current: string[] = (accountingSettings?.[kind] as string[]) || [];
    const updated = current.filter(v => v !== trimmed);
    try {
      await apiService.put('/accounting/settings', { [kind]: updated });
      setAccountingSettings({ ...(accountingSettings || {}), [kind]: updated });
    } catch (err) {
      console.error(`Failed to remove ${kind} option:`, err);
    }
  };

  // Render an <Option>'s content so user-added (non-default) values carry a
  // small × that removes them from the company settings. Stops propagation
  // and preventDefault on mousedown to keep AntD from selecting the value.
  /**
   * Add a salesperson to the company's remembered list and select them on this
   * quotation. Shared by the + button and Enter so both behave the same.
   * Duplicates are selected rather than added twice.
   */
  const addSalesperson = () => {
    const trimmed = newSalespersonInput.trim();
    if (!trimmed) return;
    if (!salespersonOptions.includes(trimmed)) {
      setSalespersonOptions([...salespersonOptions, trimmed]);
      persistCompanyOption('salespersonOptions', trimmed);
    }
    form.setFieldsValue({ salespersonName: trimmed });
    setNewSalespersonInput('');
  };

  const renderOptionContent = (
    value: string,
    defaults: string[],
    onRemove: (v: string) => void
  ) => {
    const isDefault = defaults.includes(value);
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span>{value}</span>
        {!isDefault && (
          <CloseCircleOutlined
            style={{ color: '#bbb', fontSize: 12, cursor: 'pointer' }}
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(value); }}
          />
        )}
      </div>
    );
  };

  const [form] = Form.useForm();

  // Load data on mount
  useEffect(() => {
    loadQuotations();
    loadItemTemplates();
    loadCustomers();
    loadActiveCompany();
    loadAccountingSettings();
  }, []);

  const loadAccountingSettings = async () => {
    try {
      const response: any = await apiService.get('/accounting/settings');
      setAccountingSettings(response.data || null);
    } catch (error) {
      console.error('Failed to load accounting settings:', error);
    }
  };

  const loadActiveCompany = async () => {
    try {
      const response = await companySwitcherService.getCurrentCompany();
      if (response.success && response.data.company?.code) {
        setActiveCompanyCode(response.data.company.code);
      }
    } catch (error) {
      console.error('Failed to load active company:', error);
    }
  };

  const loadQuotations = async () => {
    try {
      setLoading(true);
      // List + stats in parallel. Stats hit /quotations/stats (true DB counts)
      // so the KPI cards are accurate even when the list is filtered — the
      // old calculateStats(data) computed everything from the fetched list,
      // which mis-reported when the backend's default page size (20) capped
      // the response.
      const [data, statsData] = await Promise.all([
        quotationService.getAllQuotations(filters),
        quotationService.getQuotationStats().catch((e) => {
          console.error('Stats load failed, falling back to list-derived counts:', e);
          return null;
        }),
      ]);
      setQuotations(data);
      if (statsData) {
        setStats(statsData);
      } else {
        calculateStats(data);
      }
    } catch (error: any) {
      console.error('Failed to load quotations:', error);
      message.error(t('Failed to load quotations'));
    } finally {
      setLoading(false);
    }
  };

  const loadItemTemplates = async () => {
    try {
      const data = await quotationItemTemplateService.getAllTemplates({ isActive: true });
      setItemTemplates(Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error('Failed to load item templates:', error);
      setItemTemplates([]);
    }
  };

  const loadCustomers = async () => {
    try {
      // Use the dropdown endpoint which returns up to 500 customers (lightweight, no pagination)
      const response: any = await apiService.get('/customers/dropdown');
      let customerData: Customer[] = [];
      if (response?.customers && Array.isArray(response.customers)) {
        customerData = response.customers;
      } else if (Array.isArray(response)) {
        customerData = response;
      } else if (response?.data && Array.isArray(response.data)) {
        customerData = response.data;
      }
      setCustomers(customerData);
    } catch (error) {
      console.error('Failed to load customers:', error);
      setCustomers([]);
    }
  };

  const calculateStats = (data: Quotation[]) => {
    const calculatedStats: QuotationStats = {
      totalQuotations: data.length,
      draftQuotations: data.filter(q => q.status === 'DRAFT').length,
      sentQuotations: data.filter(q => q.status === 'SENT').length,
      acceptedQuotations: data.filter(q => q.status === 'ACCEPTED').length,
      rejectedQuotations: data.filter(q => q.status === 'REJECTED').length,
      expiredQuotations: data.filter(q => q.status === 'EXPIRED').length,
      totalAmount: data.reduce((sum, q) => sum + (Number(q.totalAmount) || 0), 0),
      acceptedAmount: data.filter(q => q.status === 'ACCEPTED').reduce((sum, q) => sum + (Number(q.totalAmount) || 0), 0),
      pendingAmount: data.filter(q => ['DRAFT', 'SENT'].includes(q.status)).reduce((sum, q) => sum + (Number(q.totalAmount) || 0), 0),
      conversionRate: data.length > 0
        ? (data.filter(q => q.status === 'ACCEPTED').length / data.length) * 100
        : 0
    };
    setStats(calculatedStats);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT': return 'default';
      case 'SENT': return 'processing';
      case 'ACCEPTED': return 'success';
      case 'REJECTED': return 'error';
      case 'EXPIRED': return 'warning';
      case 'CANCELLED': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'DRAFT': return <FileTextOutlined />;
      case 'SENT': return <SendOutlined />;
      case 'ACCEPTED': return <CheckCircleOutlined />;
      case 'REJECTED': return <CloseCircleOutlined />;
      case 'EXPIRED': return <ClockCircleOutlined />;
      case 'CANCELLED': return <CloseCircleOutlined />;
      default: return <FileTextOutlined />;
    }
  };

  // Default terms per company
  const getDefaultTerms = (companyCode: string) => {
    if (companyCode === 'SPLL') {
      return `Service Level
1. Scannable barcode label is required on all products, otherwise, $1.5 per item shall be charged.
2. SKU ID on platforms must be the same with barcode, otherwise, service for some platforms might be affected.
3. Packing list is required 2 working days before stock arrival. Each carton shall only contain one SKU, with SKU information and quantity on the surface. Otherwise, additional charge might be incurred. *Urgent inbound fee: HK$300/inbound; Inbound without packing list will be incurred HK$300/inbound; required Shoptoplus to prepare packing list: HK$200/packing list (up to 10 SKUs; HK$10/SKU thereafter).
4. For the inbound process, after counting the carton number, random inspection will be carried out. Shoptoplus Logistics Limited (hereinafter referred to as "Shoptoplus") will open 1 carton of each item and check the quantity, expiry date, food label and barcode. Stock will be rejected if any of the requirement is failed to fulfill. If there is any difference in quantity or packaging of other cartons, Shoptoplus is not responsible for the difference and related costs.
5. Shoptoplus agrees to follow and fulfill the logistics instructions and requirements from channels. Any penalty incurred or damage caused during the delivery from Shoptoplus's Warehouse to Channel Warehouse shall be fully responsible by Shoptoplus.
6. When Local Delivery Service is used, any surcharge such as entrance / parking fee shall be reimbursed at actual expense.
7. Shoptoplus is not responsible for [COMPANY NAME] (hereinafter referred to as "Client") insurance at the warehouse. Insurance of cargoes against all risks shall be borne by Client.
8. If there is any inventory loss due to a power outage over 12 hours, the maximum compensation amount is subject to twice the total amount received from that month. (If the power outage is over 12 hours, Shoptoplus will also arrange a backup generator to ensure the safety of the goods).
9. Shoptoplus shall not be liable for any loss or damage to goods tendered, stored or handled howsoever caused unless such loss or damage resulted from the failure by Shoptoplus to exercise such care in regard to them as a reasonably careful person would exercise under like circumstances. Shoptoplus shall not be liable for any loss arising from product expiration, except that the Customer has specifically requested expiration-related services and such loss is directly caused by Shoptoplus's failure to comply. In the event of loss or damage to the goods stored and maintained by Shoptoplus's failure, the liability is limited to 30% of the true value (based on the cost of goods, but not the selling or market price) of related product or HK$300 per item, whichever is lower.
10. If Client decides to store and/or ship the temperature-sensitive or easily to damage/deform during transit, including but not limited to cake with cream and/or decorations, ice-pop, sorbet, ice-cream and the related products, egg, live seafood, chilled poon chai, frozen dough & sea urchin, Shoptoplus only accepts the external packaging as the standard of responsibility definition. If the outer packaging is not damaged at the time of shipped or the delivery completed, and the products inside the packaging or its packaging is damaged, the relevant claims will not be accepted, and Client shall bear all related risks and losses. For the compensation caused by delivery services, the compensation amount is subject to the true value (based on the cost of goods, but not the selling or market price) of related products or HK$ 5,000 per case, whichever is lower.
11. All claims arising out of or in connection with this Agreement or the services are limited exclusively to the scenarios explicitly stipulated in the clauses above of this Agreement, subject to an aggregate annual cap of HK$50,000. Under no circumstances shall Shoptoplus be liable to the Customer for any indirect, consequential, incidental, special, or punitive loss or damage, including but not limited to loss of profits, revenue, goodwill, future business, or anticipated savings, regardless of whether Shoptoplus was advised of the risk of such loss or damage before or after receipt of the consignment.

Privacy
12. Any personal information provided by the Client shall only be used on fulfillment service. Shoptoplus shall not use the information for other purpose without the Client's consent.

Contract Period
13. Unless otherwise stated, the whole agreement shall be bounded by 12 months contract period (the start date equals to the service effective date) and the agreement will be renewed automatically upon maturity. After the first 12 months or a specified contract period, the agreement could be terminated by either party giving three months' written notice to the other. All outstanding payments have to settle by the termination date, otherwise Shoptoplus reserves the right to refuse the Customer's withdrawal of any goods belonging to the Customer.
14. During the contract period, the Client has the right to upgrade from existing plan to other plan option(s) with 5 business-day prior notice before entering to the next month while the original contract expiry date remains constant.
15. No downgrade is allowed within the contract period.
16. All Rates quoted in this Quotation/Agreement shall be subject to adjustment if the specifications are different from those originally submitted by Client. Shoptoplus reserves rights to review the operations and adjust the Rates subject to the actual service scopes.`;
    }
    // Default: SPL (Shoptoplus Limited)
    return `Service Level
1. Scannable barcode label is required on all products, otherwise, $1.5 per item shall be charged.
2. SKU ID on platforms must be the same with barcode, otherwise, service for some platforms might be affected.
3. Packing list is required 2 working days before stock arrival. Each carton shall only contain one SKU, with SKU information and quantity on the surface. Otherwise, additional charge might be incurred. *Urgent inbound fee: HK$300/inbound; Inbound without packing list will be incurred HK$300/inbound; required Shoptoplus to prepare packing list: HK$200/packing list (up to 10 SKUs; HK$10/SKU thereafter).
4. For the inbound process, after counting the carton number, random inspection will be carried out. Shoptoplus Logistics Limited (hereinafter referred to as "Shoptoplus") will open 1 carton of each item and check the quantity, expiry date, food label and barcode. Stock will be rejected if any of the requirement is failed to fulfill. If there is any difference in quantity or packaging of other cartons, Shoptoplus is not responsible for the difference and related costs.
5. Shoptoplus agrees to follow and fulfill the logistics instructions and requirements from channels. Any penalty incurred or damage caused during the delivery from Shoptoplus's Warehouse to Channel Warehouse shall be fully responsible by Shoptoplus.
6. When Local Delivery Service is used, any surcharge such as entrance / parking fee shall be reimbursed at actual expense.
7. Shoptoplus is not responsible for [COMPANY NAME] (hereinafter referred to as "Client") insurance at the warehouse. Insurance of cargoes against all risks shall be borne by Client.
8. If there is any inventory loss due to a power outage over 12 hours, the maximum compensation amount is subject to twice the total amount received from that month. (If the power outage is over 12 hours, Shoptoplus will also arrange a backup generator to ensure the safety of the goods).
9. Shoptoplus shall not be liable for any loss or damage to goods tendered, stored or handled howsoever caused unless such loss or damage resulted from the failure by Shoptoplus to exercise such care in regard to them as a reasonably careful person would exercise under like circumstances. Shoptoplus shall not be liable for any loss arising from product expiration, except that the Customer has specifically requested expiration-related services and such loss is directly caused by Shoptoplus's failure to comply. In the event of loss or damage to the goods stored and maintained by Shoptoplus's failure, the liability is limited to 30% of the true value (based on the cost of goods, but not the selling or market price) of related product or HK$300 per item, whichever is lower.
10. If Client decides to store and/or ship the temperature-sensitive or easily to damage/deform during transit, including but not limited to cake with cream and/or decorations, ice-pop, sorbet, ice-cream and the related products, egg, live seafood, chilled poon chai, frozen dough & sea urchin, Shoptoplus only accepts the external packaging as the standard of responsibility definition. If the outer packaging is not damaged at the time of shipped or the delivery completed, and the products inside the packaging or its packaging is damaged, the relevant claims will not be accepted, and Client shall bear all related risks and losses. For the compensation caused by delivery services, the compensation amount is subject to the true value (based on the cost of goods, but not the selling or market price) of related products or HK$ 5,000 per case, whichever is lower.
11. All claims arising out of or in connection with this Agreement or the services are limited exclusively to the scenarios explicitly stipulated in the clauses above of this Agreement, subject to an aggregate annual cap of HK$50,000. Under no circumstances shall Shoptoplus be liable to the Customer for any indirect, consequential, incidental, special, or punitive loss or damage, including but not limited to loss of profits, revenue, goodwill, future business, or anticipated savings, regardless of whether Shoptoplus was advised of the risk of such loss or damage before or after receipt of the consignment.

Privacy
12. Any personal information provided by the Client shall only be used on fulfillment service. Shoptoplus shall not use the information for other purpose without the Client's consent.

Contract Period
13. Unless otherwise stated, the whole agreement shall be bounded by 12 months contract period (the start date equals to the service effective date) and the agreement will be renewed automatically upon maturity. After the first 12 months or a specified contract period, the agreement could be terminated by either party giving three months' written notice to the other. All outstanding payments have to settle by the termination date, otherwise Shoptoplus reserves the right to refuse the Customer's withdrawal of any goods belonging to the Customer.
14. During the contract period, the Client has the right to upgrade from existing plan to other plan option(s) with 5 business-day prior notice before entering to the next month while the original contract expiry date remains constant.
15. No downgrade is allowed within the contract period.
16. All Rates quoted in this Quotation/Agreement shall be subject to adjustment if the specifications are different from those originally submitted by Client. Shoptoplus reserves rights to review the operations and adjust the Rates subject to the actual service scopes.`;
  };

  // Default payment terms per company
  const getDefaultPaymentTermsText = (companyCode: string) => {
    if (companyCode === 'SPLL') {
      return `1. All payments are non-refundable except for the deposit. Deposit shall be refundable when termination of service after the contract period. Deposit will not be refunded if Client terminates the contract within the contract period.
2. Shoptoplus shall from time to time issue statement(s) for the Service Fees due and owing on a monthly or regular basis. Client agrees to make payment to Shoptoplus in accordance with the payable amount, due date and payment remarks as set forth in the statement(s) issued by Shoptoplus without any deduction.
3. All payments shall be made in Hong Kong Dollars by direct bank deposit to "Shoptoplus Logistics Limited" within 7 days against invoice.
Bank name: The Hongkong and Shanghai Banking Corporation Limited
Bank address: 1 Queen's Road Central, Hong Kong
Swift Code: HSBCHKHHHKH
Account name: Shoptoplus Logistics Limited
Account no.: 004-747-229029-838
Currency: HKD
4. Overdue accounts are subject to interest charges at an annual rate of 2% above the Best Lending Rate and may result in service suspension.
5. Shoptoplus shall have a lien on all the goods in Shoptoplus's possession which are the property of the Client to secure the payment by the Client of all sums due to Shoptoplus, including but not limited to selling, destroying or discarding, and using any income from the sale.
6. Shoptoplus reserves the right to unilaterally terminate services, confiscate the deposit, and dispose of all goods stored in Shoptoplus in any form without prior notice if accounts are overdue for 60 days.
7. Once the Client agrees to accept Shoptoplus's services, it is deemed that the Client has read the price and remarks of this Agreement/Quotation and the terms in detail, and accepts the constraints of the terms.`;
    }
    // Default: SPL (Shoptoplus Limited)
    return `1. All payments are non-refundable except for the deposit. Deposit shall be refundable when termination of service after the contract period. Deposit will not be refunded if Client terminates the contract within the contract period.
2. Shoptoplus shall from time to time issue statement(s) for the Service Fees due and owing on a monthly or regular basis. Client agrees to make payment to Shoptoplus in accordance with the payable amount, due date and payment remarks as set forth in the statement(s) issued by Shoptoplus without any deduction.
3. All payments shall be made in Hong Kong Dollars by direct bank deposit to "Shoptoplus Limited" .
Bank name: The Hongkong and Shanghai Banking Corporation Limited
Bank address: 1 Queen's Road Central, Hong Kong
Swift Code: HSBCHKHHHKH
Account name: Shoptoplus Limited
Account no.: 004-049-628019-838
FPS ID : 105540017
Currency: HKD

4. Overdue accounts are subject to interest charges at an annual rate of 2% above the Best Lending Rate and may result in service suspension.
5. Shoptoplus shall have a lien on all the goods in Shoptoplus's possession which are the property of the Client to secure the payment by the Client of all sums due to Shoptoplus, including but not limited to selling, destroying or discarding, and using any income from the sale.
6. Shoptoplus reserves the right to unilaterally terminate services, confiscate the deposit, and dispose of all goods stored in Shoptoplus in any form without prior notice if accounts are overdue for 60 days.
7. Once the Client agrees to accept Shoptoplus's services, it is deemed that the Client has read the price and remarks of this Agreement/Quotation and the terms in detail, and accepts the constraints of the terms.`;
  };

  const handleCreate = () => {
    setSelectedQuotation(null);
    setQuotationItems([]);
    setQuotationOptions([]);
    form.resetFields();
    // Defaults for a new quotation: prefer values configured in Accounting Settings,
    // fall back to the long-standing hardcoded per-company strings so companies that
    // haven't customised settings yet don't see anything regress.
    const fallbackNotes = `*Min. charge $150/job

i)Qty below 150 Labels per SKU: $1.2/label
ii)Qty over 150 Labels per SKU: $1/label
iii)Qty over 500 Labels per SKU: $0.8/label

**Labels shall provide by client**
If required to print labels, $0.3/label will be charged as a printing fee.`;
    form.setFieldsValue({
      quotationDate: dayjs(),
      validUntil: dayjs().add(30, 'day'),
      currency: 'HKD',
      // Defaults from Accounting Settings > Quotations tab, fall back to legacy
      // hardcoded per-company strings so unconfigured companies keep working.
      notes: accountingSettings?.quotationFooterNotes || fallbackNotes,
      terms: accountingSettings?.quotationDefaultTermsConditions || getDefaultTerms(activeCompanyCode),
      paymentTermsText: accountingSettings?.quotationDefaultPaymentTerms || getDefaultPaymentTermsText(activeCompanyCode)
    });
    // Reload templates to ensure we have the latest
    loadItemTemplates();
    setIsModalVisible(true);
  };

  const handleEdit = async (record: Quotation) => {
    try {
      setLoading(true);
      // Reload templates to ensure we have the latest
      loadItemTemplates();
      const quotation = await quotationService.getQuotationById(record.id);
      setSelectedQuotation(quotation);
      form.setFieldsValue({
        customerId: quotation.customerId,
        quotationDate: quotation.quotationDate ? dayjs(quotation.quotationDate) : undefined,
        validUntil: quotation.validUntil ? dayjs(quotation.validUntil) : undefined,
        currency: quotation.currency || 'HKD',
        // Fall back to the linked user's name so a quotation saved before #17
        // still shows its salesperson when reopened.
        salespersonName: (quotation as any).salespersonName
          || (quotation as any).salesperson?.name
          || undefined,
        notes: quotation.notes,
        terms: quotation.terms,
        paymentTerms: quotation.paymentTerms,
        paymentTermsText: quotation.paymentTermsText,
        deliveryAddress: quotation.deliveryAddress,
        billingAddress: quotation.billingAddress,
        contactPerson: quotation.contactPerson,
        contactEmail: quotation.contactEmail,
        contactPhone: quotation.contactPhone,
        templateId: quotation.templateId
      });
      setQuotationItems(quotation.items || []);
      // Recalculate line total for options based on their discount type
      const fixedOptions = (quotation.options || []).map(option => {
        const quantity = Number(option.quantity) || 0;
        const unitPrice = Number(option.unitPrice) || 0;
        const discount = Number(option.discount) || 0;
        const discountType = option.discountType || 'percentage';
        let lineTotal = quantity * unitPrice;
        if (discountType === 'percentage') {
          lineTotal = lineTotal * (1 - discount / 100);
        } else {
          lineTotal = lineTotal - discount;
        }
        return {
          ...option,
          discountType,
          lineTotal
        };
      });
      setQuotationOptions(fixedOptions);
      setIsModalVisible(true);
    } catch (error: any) {
      message.error(t('Failed to load quotation details'));
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (record: Quotation) => {
    try {
      setLoading(true);
      const quotation = await quotationService.getQuotationById(record.id);
      setSelectedQuotation(quotation);
      setViewMode('detail');
    } catch (error: any) {
      message.error(t('Failed to load quotation details'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setLoading(true);
      await quotationService.deleteQuotation(id);
      message.success(t('Quotation deleted successfully'));
      loadQuotations();
    } catch (error: any) {
      message.error(t('Failed to delete quotation'));
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (id: string) => {
    try {
      setLoading(true);
      await quotationService.sendQuotation(id);
      message.success(t('Quotation sent successfully'));
      loadQuotations();
    } catch (error: any) {
      message.error(t('Failed to send quotation'));
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (id: string) => {
    try {
      setLoading(true);
      await quotationService.acceptQuotation(id);
      message.success(t('Quotation accepted successfully'));
      loadQuotations();
    } catch (error: any) {
      message.error(t('Failed to accept quotation'));
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (id: string) => {
    try {
      setLoading(true);
      await quotationService.rejectQuotation(id);
      message.success(t('Quotation rejected'));
      loadQuotations();
    } catch (error: any) {
      message.error(t('Failed to reject quotation'));
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      setLoading(true);
      await quotationService.duplicateQuotation(id);
      message.success(t('Quotation duplicated successfully'));
      loadQuotations();
    } catch (error: any) {
      message.error(t('Failed to duplicate quotation'));
    } finally {
      setLoading(false);
    }
  };

  const handleConvertToInvoice = async (id: string) => {
    try {
      setLoading(true);
      const result = await quotationService.convertToInvoice(id);
      message.success(t('Quotation converted to Invoice: {{invoiceNo}}', { invoiceNo: result.invoice.invoiceNo }));
      loadQuotations();
    } catch (error: any) {
      message.error(t('Failed to convert quotation to invoice'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (id: string) => {
    try {
      setLoading(true);
      const { blob, filename: serverFilename } = await quotationService.downloadPdf(id);
      const url = window.URL.createObjectURL(blob);

      // Prefer the filename the backend put in Content-Disposition
      // ("<Company Name> - QT-XXXX.pdf"); fall back to the quotation number only.
      const quotation = quotations.find(q => q.id === id);
      const filename = serverFilename
        || (quotation ? `${quotation.quotationNo}.pdf` : `quotation-${id}.pdf`);

      // Open PDF in a new tab
      window.open(url, '_blank');

      // Auto download the PDF
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL after a delay to allow both operations to complete
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 2000);
      message.success(t('PDF opened and downloaded'));
    } catch (error: any) {
      message.error(t('Failed to open PDF'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      const items = quotationItems.map((item, index) => ({
        description: item.description || '',
        itemCode: item.itemCode || '',
        subcategory: (item as any).subcategory || '',
        quantity: Number(item.quantity) || 0,
        unit: item.unit || 'unit(s)',
        unitPrice: Number(item.unitPrice) || 0,
        discount: Number(item.discount) || 0,
        discountType: item.discountType || 'fixed',
        taxRate: Number(item.taxRate) || 0,
        taxAmount: Number(item.taxAmount) || 0,
        lineTotal: Number(item.lineTotal) || 0,
        sortOrder: index
      }));

      const options = quotationOptions.map((option, index) => ({
        category: option.category || '',
        subcategory: option.subcategory || '',
        description: option.description || '',
        itemCode: option.itemCode || '',
        quantity: Number(option.quantity) || 0,
        unit: option.unit || '',
        unitPrice: Number(option.unitPrice) || 0,
        discount: Number(option.discount) || 0,
        discountType: option.discountType || 'percentage',
        lineTotal: Number(option.lineTotal) || 0,
        sortOrder: index
      }));

      const data = {
        customerId: values.customerId,
        quotationDate: values.quotationDate?.toISOString(),
        validUntil: values.validUntil?.toISOString(),
        currency: values.currency,
        notes: values.notes,
        terms: values.terms,
        paymentTerms: values.paymentTerms,
        paymentTermsText: values.paymentTermsText,
        deliveryAddress: values.deliveryAddress,
        billingAddress: values.billingAddress,
        contactPerson: values.contactPerson,
        contactEmail: values.contactEmail,
        contactPhone: values.contactPhone,
        templateId: values.templateId,
        salespersonName: values.salespersonName || undefined,
        items,
        options: options.length > 0 ? options : undefined
      };

      if (selectedQuotation) {
        await quotationService.updateQuotation(selectedQuotation.id, data as UpdateQuotationInput);
        message.success(t('Quotation updated successfully'));
      } else {
        await quotationService.createQuotation(data as CreateQuotationInput);
        message.success(t('Quotation created successfully'));
      }

      setIsModalVisible(false);
      form.resetFields();
      setQuotationItems([]);
      setQuotationOptions([]);
      loadQuotations();
    } catch (error: any) {
      message.error(selectedQuotation ? t('Failed to update quotation') : t('Failed to create quotation'));
    } finally {
      setLoading(false);
    }
  };

  const addQuotationItem = () => {
    setQuotationItems([
      ...quotationItems,
      {
        description: '',
        itemCode: '',
        quantity: 1,
        unit: 'unit(s)',
        unitPrice: 0,
        discount: 0,
        discountType: 'fixed' as const,
        taxRate: 0,
        taxAmount: 0,
        lineTotal: 0,
        sortOrder: quotationItems.length
      }
    ]);
  };

  const removeQuotationItem = (index: number) => {
    const newItems = quotationItems.filter((_, i) => i !== index);
    setQuotationItems(newItems);
  };

  const updateQuotationItem = (index: number, field: string, value: any) => {
    const newItems = [...quotationItems];
    newItems[index] = { ...newItems[index], [field]: value };

    // Recalculate line total
    if (['quantity', 'unitPrice', 'discount', 'discountType', 'taxRate'].includes(field)) {
      const item = newItems[index];
      const quantity = Number(item.quantity) || 0;
      const unitPrice = Number(item.unitPrice) || 0;
      const discount = Number(item.discount) || 0;
      const discountType = item.discountType || 'fixed';
      const taxRate = Number(item.taxRate) || 0;

      let subtotal = quantity * unitPrice;
      if (discountType === 'percentage') {
        subtotal = subtotal * (1 - discount / 100);
      } else {
        subtotal = subtotal - discount;
      }

      const taxAmount = subtotal * (taxRate / 100);
      newItems[index].taxAmount = taxAmount;
      newItems[index].lineTotal = subtotal + taxAmount;
    }

    setQuotationItems(newItems);
  };

  const calculateTotals = () => {
    const subtotal = quotationItems.reduce((sum, item) => {
      const qty = Number(item.quantity) || 0;
      const price = Number(item.unitPrice) || 0;
      return sum + (qty * price);
    }, 0);

    const discountTotal = quotationItems.reduce((sum, item) => {
      const qty = Number(item.quantity) || 0;
      const price = Number(item.unitPrice) || 0;
      const discount = Number(item.discount) || 0;
      const discountType = item.discountType || 'fixed';
      if (discountType === 'percentage') {
        return sum + (qty * price * discount / 100);
      }
      return sum + discount;
    }, 0);

    const taxTotal = quotationItems.reduce((sum, item) => sum + (Number(item.taxAmount) || 0), 0);
    const total = quotationItems.reduce((sum, item) => sum + (Number(item.lineTotal) || 0), 0);

    return { subtotal, discountTotal, taxTotal, total };
  };

  const calculateOptionsTotals = () => {
    const subtotal = quotationOptions.reduce((sum, option) => {
      const qty = Number(option.quantity) || 0;
      const price = Number(option.unitPrice) || 0;
      return sum + (qty * price);
    }, 0);

    const discountTotal = quotationOptions.reduce((sum, option) => {
      const qty = Number(option.quantity) || 0;
      const price = Number(option.unitPrice) || 0;
      const discount = Number(option.discount) || 0;
      const discountType = option.discountType || 'percentage';
      if (discountType === 'percentage') {
        return sum + (qty * price * discount / 100);
      }
      return sum + discount;
    }, 0);

    const total = quotationOptions.reduce((sum, option) => sum + (Number(option.lineTotal) || 0), 0);

    return { subtotal, discountTotal, total };
  };

  // Options management functions
  const addQuotationOption = () => {
    setQuotationOptions([
      ...quotationOptions,
      {
        category: 'Options',
        description: '',
        itemCode: '',
        quantity: 1,
        unit: 'unit(s)',
        unitPrice: 0,
        discount: 0,
        discountType: 'percentage' as const,
        lineTotal: 0,
        sortOrder: quotationOptions.length
      }
    ]);
  };

  const removeQuotationOption = (index: number) => {
    const newOptions = quotationOptions.filter((_, i) => i !== index);
    setQuotationOptions(newOptions);
  };

  const updateQuotationOption = (index: number, field: string, value: any) => {
    const newOptions = [...quotationOptions];
    newOptions[index] = { ...newOptions[index], [field]: value };

    // Recalculate line total when relevant fields change
    if (['quantity', 'unitPrice', 'discount', 'discountType'].includes(field)) {
      const option = newOptions[index];
      const quantity = Number(option.quantity) || 0;
      const unitPrice = Number(option.unitPrice) || 0;
      const discount = Number(option.discount) || 0;
      const discountType = option.discountType || 'percentage';

      let subtotal = quantity * unitPrice;
      if (discountType === 'percentage') {
        subtotal = subtotal * (1 - discount / 100);
      } else {
        subtotal = subtotal - discount;
      }

      newOptions[index].lineTotal = subtotal;
    }

    setQuotationOptions(newOptions);
  };

  // Apply template to option row (for Options category templates)
  const applyTemplateToOptionRow = (index: number, templateId: string) => {
    const template = itemTemplates.find(t => t.id === templateId);
    if (template) {
      const newOptions = [...quotationOptions];
      const quantity = newOptions[index].quantity || 1;
      const unitPrice = Number(template.unitPrice) || 0;
      const discount = Number(template.discount) || 0;
      const discountType = template.discountType || 'percentage';

      let subtotal = quantity * unitPrice;
      if (discountType === 'percentage') {
        subtotal = subtotal * (1 - discount / 100);
      } else {
        subtotal = subtotal - discount;
      }

      newOptions[index] = {
        ...newOptions[index],
        itemCode: template.itemCode,
        description: template.description,
        category: template.category || '',
        subcategory: template.subcategory || '',
        unit: template.unit || newOptions[index].unit,
        unitPrice: unitPrice,
        discount: discount,
        discountType: discountType,
        lineTotal: subtotal
      };

      setQuotationOptions(newOptions);
    }
  };

  // Get templates filtered by category for Options
  const getOptionTemplates = () => {
    return itemTemplates.filter(t =>
      t.category?.toLowerCase() === 'options'
    );
  };

  // Get templates for regular items (Main Item category or uncategorized)
  const getItemTemplates = () => {
    return itemTemplates.filter(t =>
      !t.category ||
      t.category?.toLowerCase() === 'main item'
    );
  };

  // Apply template to a specific row
  const applyTemplateToRow = (index: number, templateId: string) => {
    const template = itemTemplates.find(t => t.id === templateId);
    if (template) {
      const newItems = [...quotationItems];
      const quantity = newItems[index].quantity || 1;
      const unitPrice = Number(template.unitPrice) || 0;
      const discount = Number(template.discount) || 0;
      const discountType = template.discountType || 'fixed';
      const taxRate = Number(template.taxRate) || 0;

      let subtotal = quantity * unitPrice;
      if (discountType === 'percentage') {
        subtotal = subtotal * (1 - discount / 100);
      } else {
        subtotal = subtotal - discount;
      }

      const taxAmount = subtotal * (taxRate / 100);

      newItems[index] = {
        ...newItems[index],
        itemCode: template.itemCode,
        description: template.description,
        subcategory: template.subcategory || newItems[index].subcategory,
        unit: template.unit || newItems[index].unit,
        unitPrice: unitPrice,
        discount: discount,
        discountType: discountType,
        taxRate: taxRate,
        taxAmount: taxAmount,
        lineTotal: subtotal + taxAmount
      };

      setQuotationItems(newItems);
    }
  };

  const getActionMenuItems = (record: Quotation): MenuProps['items'] => {
    const items: MenuProps['items'] = [];

    if (record.status === 'SENT') {
      items.push(
        {
          key: 'accept',
          icon: <CheckCircleOutlined />,
          label: t('Mark as Accepted'),
          onClick: () => handleAccept(record.id)
        },
        {
          key: 'reject',
          icon: <CloseCircleOutlined />,
          label: t('Mark as Rejected'),
          onClick: () => handleReject(record.id)
        }
      );
    }

    if (record.status === 'ACCEPTED') {
      items.push(
        {
          key: 'convertToInvoice',
          icon: <DollarOutlined />,
          label: t('Convert to Invoice'),
          onClick: () => handleConvertToInvoice(record.id)
        }
      );
    }

    return items;
  };

  // The salesperson a quotation is shown under. Three sources in priority
  // order, matching what the column renders: the typed name (#17), a quotation
  // still linked to a user account, and finally whoever created it.
  const NO_SALESPERSON = '__none__';
  const salespersonOf = (r: any): string =>
    r.salespersonName || r.salesperson?.name || r.user?.name || '';

  // Offered options come from the quotations on screen, not a fixed list, so
  // the filter always matches what is actually there — and fills itself in as
  // the team starts typing real names.
  const salespeopleInList = React.useMemo(() => {
    const names = new Set<string>();
    let anyBlank = false;
    for (const r of quotations) {
      const n = salespersonOf(r);
      if (n) names.add(n); else anyBlank = true;
    }
    const sorted = Array.from(names).sort((a, b) => a.localeCompare(b));
    return { names: sorted, anyBlank };
  }, [quotations]);

  // Search and the three filters combine with AND; several values inside one
  // filter are OR. So "Sent or Accepted, by Admin, in September" is one query.
  const filteredQuotations = React.useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return quotations.filter(r => {
      if (q) {
        const hit =
          (r.quotationNo || '').toLowerCase().includes(q) ||
          (r.customer?.name || '').toLowerCase().includes(q) ||
          salespersonOf(r).toLowerCase().includes(q);
        if (!hit) return false;
      }

      if (statusFilter.length && !statusFilter.includes(r.status)) return false;

      if (salespersonFilter.length) {
        const name = salespersonOf(r);
        const match = name
          ? salespersonFilter.includes(name)
          : salespersonFilter.includes(NO_SALESPERSON);
        if (!match) return false;
      }

      if (dateRange) {
        // Compare on the date alone: quotationDate carries a time, and a range
        // picked as 1st-30th must include everything dated the 30th.
        const d = String(r.quotationDate || '').slice(0, 10);
        if (!d || d < dateRange[0] || d > dateRange[1]) return false;
      }

      return true;
    });
  }, [quotations, searchText, statusFilter, salespersonFilter, dateRange]);

  const columns: ColumnsType<Quotation> = [
    {
      title: t('Quotation #'),
      dataIndex: 'quotationNo',
      key: 'quotationNo',
      sorter: (a, b) => (a.quotationNo || '').localeCompare(b.quotationNo || ''),
      render: (text, record) => (
        <Button type="link" onClick={() => handleView(record)}>
          {text}
        </Button>
      )
    },
    {
      title: t('Customer'),
      dataIndex: ['customer', 'name'],
      key: 'customer',
      render: (_, record) => record.customer?.name || '-'
    },
    {
      title: t('Salesperson'),
      key: 'salesperson',
      // Falls back to whoever created the quote, which is what older records
      // hold — they pre-date this field.
      render: (_, record) => record.salespersonName || record.salesperson?.name || record.user?.name || '-'
    },
    {
      title: t('Date'),
      dataIndex: 'quotationDate',
      key: 'quotationDate',
      sorter: (a, b) => dayjs(a.quotationDate).valueOf() - dayjs(b.quotationDate).valueOf(),
      defaultSortOrder: 'descend',
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : '-'
    },
    {
      title: t('Valid Until'),
      dataIndex: 'validUntil',
      key: 'validUntil',
      render: (date, record) => {
        if (!date) return '-';
        const isExpired = dayjs(date).isBefore(dayjs()) && record.status === 'SENT';
        return (
          <span style={{ color: isExpired ? '#ff4d4f' : undefined }}>
            {dayjs(date).format('YYYY-MM-DD')}
            {isExpired && <ExclamationCircleOutlined style={{ marginLeft: 4 }} />}
          </span>
        );
      }
    },
    {
      title: t('Total Amount'),
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      align: 'right',
      render: (amount, record) => `${record.currency || 'HKD'} ${amount?.toLocaleString() || 0}`
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag icon={getStatusIcon(status)} color={getStatusColor(status)}>
          {status}
        </Tag>
      )
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 450,
      render: (_, record) => (
        <Space size={4} wrap>
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleView(record)}>{t('View')}</Button>
          {(record.status === 'DRAFT' || record.status === 'SENT') && (
            <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>{t('Edit')}</Button>
          )}
          <Button size="small" icon={<PrinterOutlined />} onClick={() => handleDownloadPdf(record.id)}>{t('Print')}</Button>
          <Button size="small" icon={<CopyOutlined />} onClick={() => handleDuplicate(record.id)}>{t('Duplicate')}</Button>
          {record.status === 'DRAFT' && (
            <Button size="small" type="primary" icon={<SendOutlined />} onClick={() => handleSend(record.id)}>{t('Send')}</Button>
          )}
          {record.status === 'DRAFT' && (
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>{t('Delete')}</Button>
          )}
          {record.status === 'SENT' && (
            <Button size="small" type="primary" icon={<CheckCircleOutlined />} onClick={() => handleAccept(record.id)}>{t('Accept')}</Button>
          )}
          {record.status === 'SENT' && (
            <Button size="small" danger icon={<CloseCircleOutlined />} onClick={() => handleReject(record.id)}>{t('Reject')}</Button>
          )}
          {record.status === 'ACCEPTED' && (
            <Button size="small" icon={<DollarOutlined />} onClick={() => handleConvertToInvoice(record.id)}>{t('To Invoice')}</Button>
          )}
        </Space>
      )
    }
  ];

  // Free text item columns - no product dependency
  const itemColumns: ColumnsType<Partial<QuotationItem>> = [
    {
      title: t('Template'),
      dataIndex: 'template',
      key: 'template',
      // A picker, not data: once a template is applied the box goes back to
      // showing its placeholder, so the column never displays anything longer
      // than "Select template". The width it used to take belongs to Item
      // Title, which holds real content.
      width: 160,
      render: (_, _record, index) => (
        <Select
          placeholder={t('Select template')}
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: '100%' }}
          // antd matches the dropdown to the box unless told otherwise, so a
          // narrow column would clip the option labels — they run to 91
          // characters. false lets the list size itself to its content instead,
          // rather than to a number that happens to fit today's templates. The
          // max-width keeps a pathological label from filling the screen.
          popupMatchSelectWidth={false}
          dropdownStyle={{ maxWidth: 720 }}
          onChange={(value) => value && applyTemplateToRow(index, value)}
          options={getItemTemplates().map(t => ({
            value: t.id,
            label: `${t.itemCode} - ${t.description?.substring(0, 30)}${(t.description?.length || 0) > 30 ? '...' : ''}`
          }))}
          size="small"
        />
      )
    },
    {
      title: t('Item Title'),
      dataIndex: 'itemCode',
      key: 'itemCode',
      // Half of the real titles run past 120px — "e-Commerce Platform Transit
      // Warehouse HKTVmall Basic Storage Plan" is 65 characters. In a
      // single-line Input the overflow scrolls sideways under the cursor, which
      // is the "disable mouse scroll" complaint. A TextArea wraps instead, so
      // the whole title is visible and there is nothing inside it to scroll.
      // Widened again with the space reclaimed from the Template picker, so the
      // common titles now fit on one line rather than wrapping.
      width: 320,
      render: (_, record, index) => (
        <TextArea
          value={record.itemCode}
          onChange={(e) => updateQuotationItem(index, 'itemCode', e.target.value)}
          placeholder={t('Item title')}
          autoSize={{ minRows: 1 }}
          size="small"
        />
      )
    },
    {
      title: t('Subcategory'),
      dataIndex: 'subcategory',
      key: 'subcategory',
      width: 140,
      render: (_, record, index) => (
        <Select
          value={(record as any).subcategory || undefined}
          onChange={(value) => updateQuotationItem(index, 'subcategory', value || '')}
          style={{ width: '100%' }}
          placeholder={t('Optional')}
          allowClear
          size="small"
          optionLabelProp="label"
          dropdownRender={(menu) => (
            <>
              {menu}
              <Divider style={{ margin: '4px 0' }} />
              <div style={{ display: 'flex', gap: 4, padding: '4px 8px' }}>
                <Input
                  size="small"
                  placeholder={t('New subcategory')}
                  value={newSubcategoryInput}
                  onChange={(e) => setNewSubcategoryInput(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  style={{ flex: 1 }}
                />
                <Button
                  size="small"
                  type="text"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    const trimmed = newSubcategoryInput.trim();
                    if (trimmed && !subcategoryOptions.includes(trimmed)) {
                      setSubcategoryOptions([...subcategoryOptions, trimmed]);
                      updateQuotationItem(index, 'subcategory', trimmed);
                      setNewSubcategoryInput('');
                      persistCompanyOption('subcategoryOptions', trimmed);
                    }
                  }}
                />
              </div>
            </>
          )}
        >
          {subcategoryOptions.map(s => (
            <Option key={s} value={s} label={s}>
              {renderOptionContent(s, DEFAULT_SUBCATEGORY_OPTIONS, v => removeCompanyOption('subcategoryOptions', v, setSubcategoryOptions))}
            </Option>
          ))}
        </Select>
      )
    },
    {
      title: t('Description'),
      dataIndex: 'description',
      key: 'description',
      width: 300,
      render: (_, record, index) => (
        <TextArea
          value={record.description}
          onChange={(e) => updateQuotationItem(index, 'description', e.target.value)}
          placeholder={t('Enter item description')}
          // No maxRows: with a cap the box scrolls its own content once the
          // description passes 8 lines, so a wheel over it moves the box
          // instead of the dialog. Growing to fit means no inner scrollbar.
          autoSize={{ minRows: 2 }}
          size="small"
        />
      )
    },
    {
      title: t('Qty'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 70,
      render: (_, record, index) => (
        <InputNumber
          value={record.quantity}
          onChange={(value) => updateQuotationItem(index, 'quantity', value || 0)}
          min={0}
          style={{ width: '100%' }}
          size="small"
        />
      )
    },
    {
      title: t('Unit'),
      dataIndex: 'unit',
      key: 'unit',
      width: 100,
      render: (_, record, index) => (
        <Select
          value={record.unit}
          onChange={(value) => updateQuotationItem(index, 'unit', value)}
          style={{ width: '100%' }}
          placeholder={t('Unit')}
          size="small"
          optionLabelProp="label"
          dropdownRender={(menu) => (
            <>
              {menu}
              <Divider style={{ margin: '4px 0' }} />
              <div style={{ display: 'flex', gap: 4, padding: '4px 8px' }}>
                <Input
                  size="small"
                  placeholder={t('New unit')}
                  value={newUnitInput}
                  onChange={(e) => setNewUnitInput(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  style={{ flex: 1 }}
                />
                <Button
                  size="small"
                  type="text"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    const trimmed = newUnitInput.trim();
                    if (trimmed && !unitOptions.includes(trimmed)) {
                      setUnitOptions([...unitOptions, trimmed]);
                      updateQuotationItem(index, 'unit', trimmed);
                      setNewUnitInput('');
                      persistCompanyOption('unitOptions', trimmed);
                    }
                  }}
                />
              </div>
            </>
          )}
        >
          {unitOptions.map(u => (
            <Option key={u} value={u} label={u}>
              {renderOptionContent(u, DEFAULT_UNIT_OPTIONS, v => removeCompanyOption('unitOptions', v, setUnitOptions))}
            </Option>
          ))}
        </Select>
      )
    },
    {
      title: t('Unit Price'),
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 120,
      render: (_, record, index) => (
        <InputNumber
          value={record.unitPrice}
          onChange={(value) => updateQuotationItem(index, 'unitPrice', value || 0)}
          min={0}
          style={{ width: '100%' }}
          formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, '')) || 0}
          size="small"
        />
      )
    },
    {
      title: t('Discount'),
      dataIndex: 'discount',
      key: 'discount',
      width: 130,
      render: (_, record, index) => (
        <Space.Compact style={{ width: '100%' }}>
          <InputNumber
            value={record.discount}
            onChange={(value) => updateQuotationItem(index, 'discount', value || 0)}
            min={0}
            style={{ width: '60%' }}
            size="small"
          />
          <Select
            value={record.discountType || 'fixed'}
            onChange={(value) => updateQuotationItem(index, 'discountType', value)}
            style={{ width: '40%' }}
            size="small"
          >
            <Option value="fixed">$</Option>
            <Option value="percentage">%</Option>
          </Select>
        </Space.Compact>
      )
    },
    // Tax column removed from the quotation item editor per accounting team request.
    // taxRate/taxAmount fields remain on the data model so existing quotations stay
    // intact; new line items default to taxRate = 0 (set in addQuotationItem).
    {
      title: t('Line Total'),
      dataIndex: 'lineTotal',
      key: 'lineTotal',
      width: 100,
      align: 'right',
      render: (lineTotal) => <Text strong>${(lineTotal || 0).toLocaleString()}</Text>
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      fixed: 'right',
      render: (_, record, index) => (
        <Button
          type="text"
          icon={<DeleteOutlined />}
          danger
          onClick={() => removeQuotationItem(index)}
          size="small"
        />
      )
    }
  ];

  // Options table columns
  const optionColumns: ColumnsType<Partial<QuotationOption>> = [
    {
      title: t('Template'),
      dataIndex: 'template',
      key: 'template',
      // Same as the items table: a picker, so it needs no more than its
      // placeholder. See the note there.
      width: 160,
      render: (_, _record, index) => (
        <Select
          placeholder={t('Select template')}
          allowClear
          showSearch
          popupMatchSelectWidth={false}
          dropdownStyle={{ maxWidth: 720 }}
          optionFilterProp="label"
          style={{ width: '100%' }}
          onChange={(value) => value && applyTemplateToOptionRow(index, value)}
          options={getOptionTemplates().map(t => ({
            value: t.id,
            label: `${t.itemCode} - ${t.description?.substring(0, 30)}${(t.description?.length || 0) > 30 ? '...' : ''}`
          }))}
          size="small"
        />
      )
    },
    {
      // Mirrors the Item Title column on the main items table — backed by
      // QuotationOption.itemCode (already on the schema), and wrapping for the
      // same reason, and widened with the space reclaimed from Template.
      title: t('Item Title'),
      dataIndex: 'itemCode',
      key: 'itemCode',
      width: 290,
      render: (_, record, index) => (
        <TextArea
          value={record.itemCode}
          onChange={(e) => updateQuotationOption(index, 'itemCode', e.target.value)}
          placeholder={t('Item title')}
          autoSize={{ minRows: 1 }}
          size="small"
        />
      )
    },
    {
      title: t('Subcategory'),
      dataIndex: 'subcategory',
      key: 'subcategory',
      width: 160,
      render: (_, record, index) => (
        <Select
          value={record.subcategory}
          onChange={(value) => updateQuotationOption(index, 'subcategory', value)}
          placeholder={t('Subcategory')}
          style={{ width: '100%' }}
          allowClear
          size="small"
        >
          <Option value="Extra Storage">{t('Extra Storage')}</Option>
          <Option value="Value-added Service">{t('Value-added Service')}</Option>
        </Select>
      )
    },
    {
      title: t('Description'),
      dataIndex: 'description',
      key: 'description',
      width: 280,
      render: (_, record, index) => (
        <TextArea
          value={record.description}
          onChange={(e) => updateQuotationOption(index, 'description', e.target.value)}
          placeholder={t('Enter option description')}
          // No maxRows: with a cap the box scrolls its own content once the
          // description passes 8 lines, so a wheel over it moves the box
          // instead of the dialog. Growing to fit means no inner scrollbar.
          autoSize={{ minRows: 2 }}
          size="small"
        />
      )
    },
    {
      title: t('Qty'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 70,
      render: (_, record, index) => (
        <InputNumber
          value={record.quantity}
          onChange={(value) => updateQuotationOption(index, 'quantity', value || 0)}
          min={0}
          style={{ width: '100%' }}
          size="small"
        />
      )
    },
    {
      title: t('Unit'),
      dataIndex: 'unit',
      key: 'unit',
      width: 100,
      render: (_, record, index) => (
        <Select
          value={record.unit}
          onChange={(value) => updateQuotationOption(index, 'unit', value)}
          style={{ width: '100%' }}
          size="small"
          optionLabelProp="label"
          dropdownRender={(menu) => (
            <>
              {menu}
              <Divider style={{ margin: '4px 0' }} />
              <div style={{ display: 'flex', gap: 4, padding: '4px 8px' }}>
                <Input
                  size="small"
                  placeholder={t('New unit')}
                  value={newUnitInput}
                  onChange={(e) => setNewUnitInput(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  style={{ flex: 1 }}
                />
                <Button
                  size="small"
                  type="text"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    const trimmed = newUnitInput.trim();
                    if (trimmed && !unitOptions.includes(trimmed)) {
                      setUnitOptions([...unitOptions, trimmed]);
                      updateQuotationOption(index, 'unit', trimmed);
                      setNewUnitInput('');
                      persistCompanyOption('unitOptions', trimmed);
                    }
                  }}
                />
              </div>
            </>
          )}
        >
          {unitOptions.map(u => (
            <Option key={u} value={u} label={u}>
              {renderOptionContent(u, DEFAULT_UNIT_OPTIONS, v => removeCompanyOption('unitOptions', v, setUnitOptions))}
            </Option>
          ))}
        </Select>
      )
    },
    {
      title: t('Unit Price'),
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 120,
      render: (_, record, index) => (
        <InputNumber
          value={record.unitPrice}
          onChange={(value) => updateQuotationOption(index, 'unitPrice', value || 0)}
          min={0}
          style={{ width: '100%' }}
          formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, '')) || 0}
          size="small"
        />
      )
    },
    {
      title: t('Discount'),
      dataIndex: 'discount',
      key: 'discount',
      width: 130,
      render: (_, record, index) => (
        <Space.Compact style={{ width: '100%' }}>
          <InputNumber
            value={record.discount}
            onChange={(value) => updateQuotationOption(index, 'discount', value || 0)}
            min={0}
            style={{ width: '60%' }}
            size="small"
          />
          <Select
            value={record.discountType || 'percentage'}
            onChange={(value) => updateQuotationOption(index, 'discountType', value)}
            style={{ width: '40%' }}
            size="small"
          >
            <Option value="fixed">$</Option>
            <Option value="percentage">%</Option>
          </Select>
        </Space.Compact>
      )
    },
    {
      title: t('Line Total'),
      dataIndex: 'lineTotal',
      key: 'lineTotal',
      width: 100,
      align: 'right',
      render: (lineTotal) => <Text strong>${(lineTotal || 0).toLocaleString()}</Text>
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      fixed: 'right',
      render: (_, record, index) => (
        <Button
          type="text"
          icon={<DeleteOutlined />}
          danger
          onClick={() => removeQuotationOption(index)}
          size="small"
        />
      )
    }
  ];

  const renderQuotationModal = () => {
    const totals = calculateTotals();
    const optionsTotals = calculateOptionsTotals();

    return (
      <Modal
        title={selectedQuotation ? t('Edit Quotation') : t('Create Quotation')}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setQuotationItems([]);
          setQuotationOptions([]);
          setSelectedQuotation(null);
        }}
        footer={null}
        width={1200}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="customerId"
                label={t('Customer')}
                rules={[{ required: true, message: t('Please select a customer') }]}
              >
                <Select
                  placeholder={t('Select customer')}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {customers.map(customer => (
                    <Option key={customer.id} value={customer.id}>
                      {customer.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="quotationDate"
                label={t('Quotation Date')}
                rules={[{ required: true, message: t('Please select date') }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="validUntil"
                label={t('Valid Until')}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="currency" label={t('Currency')}>
                <Select>
                  <Option value="HKD">HKD</Option>
                  <Option value="USD">USD</Option>
                  <Option value="CNY">CNY</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="paymentTerms" label={t('Payment Period')}>
                <Select
                  placeholder={t('Select payment period')}
                  allowClear
                  optionLabelProp="label"
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <Divider style={{ margin: '4px 0' }} />
                      <div style={{ display: 'flex', gap: 4, padding: '4px 8px' }}>
                        <Input
                          size="small"
                          placeholder={t('New payment period')}
                          value={newPaymentPeriodInput}
                          onChange={(e) => setNewPaymentPeriodInput(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                          style={{ flex: 1 }}
                        />
                        <Button
                          size="small"
                          type="text"
                          icon={<PlusOutlined />}
                          onClick={() => {
                            const trimmed = newPaymentPeriodInput.trim();
                            if (trimmed && !paymentPeriodOptions.includes(trimmed)) {
                              setPaymentPeriodOptions([...paymentPeriodOptions, trimmed]);
                              form.setFieldValue('paymentTerms', trimmed);
                              setNewPaymentPeriodInput('');
                              persistCompanyOption('paymentPeriodOptions', trimmed);
                            }
                          }}
                        />
                      </div>
                    </>
                  )}
                >
                  {paymentPeriodOptions.map(p => (
                    <Option key={p} value={p} label={p}>
                      {renderOptionContent(p, DEFAULT_PAYMENT_PERIOD_OPTIONS, v => removeCompanyOption('paymentPeriodOptions', v, setPaymentPeriodOptions))}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="contactPerson" label={t('Contact Person')}>
                <Input placeholder={t('Contact person name')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="contactEmail" label={t('Contact Email')}>
                <Input placeholder="contact@example.com" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="contactPhone" label={t('Contact Phone')}>
                <Input placeholder="+852 1234 5678" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="deliveryAddress" label={t('Delivery Address')}>
                <TextArea rows={2} placeholder={t('Delivery address')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="billingAddress" label={t('Billing Address')}>
                <TextArea rows={2} placeholder={t('Billing address')} />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <Title level={5}>{t('Quotation Items')}</Title>
            <Space>
              <Button type="primary" icon={<PlusOutlined />} onClick={addQuotationItem}>
                {t('Add Item')}
                                          </Button>
            </Space>
          </div>

          <Table
            columns={[itemsDragHandleColumn, ...itemColumns]}
            dataSource={quotationItems}
            pagination={false}
            rowKey={(_, index) => index?.toString() || '0'}
            style={{ marginBottom: 16 }}
            size="small"
            // Sum of the item column widths above (30+160+320+140+300+70+100+120+130+100+50).
            scroll={{ x: 1520 }}
            onRow={itemsRowDrag}
          />

          <Row gutter={16} justify="end" style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Card size="small" title={t('Items Total')}>
                <Statistic
                  title={t('Subtotal')}
                  value={totals.subtotal}
                  prefix="$"
                  precision={2}
                />
                <Statistic
                  title={t('Discount')}
                  value={totals.discountTotal}
                  prefix="-$"
                  precision={2}
                  style={{ marginTop: 8 }}
                />
                <Statistic
                  title={t('Tax')}
                  value={totals.taxTotal}
                  prefix="$"
                  precision={2}
                  style={{ marginTop: 8 }}
                />
                <Divider style={{ margin: '8px 0' }} />
                <Statistic
                  title={t('Total')}
                  value={totals.total}
                  prefix="$"
                  precision={2}
                  valueStyle={{ fontWeight: 'bold' }}
                />
              </Card>
            </Col>
          </Row>

          <Divider />

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <Title level={5}>{t('Options (Optional Services)')}</Title>
            <Space>
              <Button icon={<PlusOutlined />} onClick={addQuotationOption}>
                {t('Add Option')}
                                          </Button>
            </Space>
          </div>

          <Table
            columns={[optionsDragHandleColumn, ...optionColumns]}
            dataSource={quotationOptions}
            pagination={false}
            rowKey={(_, index) => `option-${index?.toString() || '0'}`}
            // Sum of the option column widths above (30+160+290+160+280+70+100+120+130+100+50).
            scroll={{ x: 1490 }}
            style={{ marginBottom: 16 }}
            size="small"
            locale={{ emptyText: t('No options added. Options are displayed on a separate page in the PDF.') }}
            onRow={optionsRowDrag}
          />

          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={12}>
              {/*
                A typed name with a remembered list, not a link to a login
                account (client feedback #17). The account list could only offer
                users, so it showed things like "Pack_Scanning" and
                "accounts@shoptoplus.com" while real salespeople were missing.
                Type a name, press +, and it is offered to the whole company
                from then on — the same control as Subcategory and Unit.
              */}
              <Form.Item
                name="salespersonName"
                label={t('Salesperson')}
                tooltip={t('Who the sale belongs to. Shown in the quotation list; not printed on the PDF.')}
              >
                <Select
                  placeholder={t('Select or add a salesperson')}
                  allowClear
                  showSearch
                  optionLabelProp="label"
                  optionFilterProp="label"
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <Divider style={{ margin: '4px 0' }} />
                      <div style={{ display: 'flex', gap: 4, padding: '4px 8px' }}>
                        <Input
                          size="small"
                          placeholder={t('New salesperson')}
                          value={newSalespersonInput}
                          onChange={(e) => setNewSalespersonInput(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                          onPressEnter={() => addSalesperson()}
                          style={{ flex: 1 }}
                        />
                        <Button
                          size="small"
                          type="text"
                          icon={<PlusOutlined />}
                          onClick={() => addSalesperson()}
                        />
                      </div>
                    </>
                  )}
                >
                  {salespersonOptions.map(name => (
                    <Option key={name} value={name} label={name}>
                      {renderOptionContent(name, [], v =>
                        removeCompanyOption('salespersonOptions', v, setSalespersonOptions))}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="notes" label={t('Notes')}>
                <TextArea rows={3} placeholder={t('Notes to customer')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="terms" label={t('Terms & Conditions')}>
                <TextArea rows={3} placeholder={t('Terms and conditions')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="paymentTermsText" label={t('Payment Terms')}>
                <TextArea rows={3} placeholder={t('Payment terms and conditions')} />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ textAlign: 'right', marginTop: 24 }}>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>
                {t('Cancel')}
                                          </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {selectedQuotation ? t('Update Quotation') : t('Create Quotation')}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    );
  };

  const renderDetailModal = () => {
    if (!selectedQuotation) return null;

    return (
      <Modal
        title={`Quotation ${selectedQuotation.quotationNo}`}
        open={viewMode === 'detail'}
        onCancel={() => {
          setViewMode('list');
          setSelectedQuotation(null);
        }}
        footer={
          <Space>
            <Button onClick={() => {
              setViewMode('list');
              setSelectedQuotation(null);
            }}>
              {t('Close')}
                                </Button>
            <Button icon={<DownloadOutlined />} onClick={() => handleDownloadPdf(selectedQuotation.id)}>
              {t('Download PDF')}
                                </Button>
            {selectedQuotation.status === 'DRAFT' && (
              <Button type="primary" icon={<SendOutlined />} onClick={() => handleSend(selectedQuotation.id)}>
                {t('Send to Customer')}
                                      </Button>
            )}
          </Space>
        }
        width={900}
      >
        <Descriptions column={2} bordered>
          <Descriptions.Item label={t('Quotation Number')}>
            {selectedQuotation.quotationNo}
          </Descriptions.Item>
          <Descriptions.Item label={t('Status')}>
            <Tag icon={getStatusIcon(selectedQuotation.status)} color={getStatusColor(selectedQuotation.status)}>
              {selectedQuotation.status}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('Customer')}>
            {selectedQuotation.customer?.name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('Contact Person')}>
            {selectedQuotation.contactPerson || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('Quotation Date')}>
            {selectedQuotation.quotationDate ? dayjs(selectedQuotation.quotationDate).format('YYYY-MM-DD') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('Valid Until')}>
            {selectedQuotation.validUntil ? dayjs(selectedQuotation.validUntil).format('YYYY-MM-DD') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('Currency')}>
            {selectedQuotation.currency || 'HKD'}
          </Descriptions.Item>
          <Descriptions.Item label={t('Payment Period')}>
            {selectedQuotation.paymentTerms || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('Delivery Address')} span={2}>
            {selectedQuotation.deliveryAddress || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('Billing Address')} span={2}>
            {selectedQuotation.billingAddress || '-'}
          </Descriptions.Item>
        </Descriptions>

        <Divider />

        <Title level={5}>{t('Items')}</Title>
        <Table
          columns={[
            { title: t('Item Title'), dataIndex: 'itemCode', key: 'itemCode', width: 120 },
            { title: t('Description'), dataIndex: 'description', key: 'description' },
            { title: t('Qty'), dataIndex: 'quantity', key: 'quantity', align: 'right' as const, width: 80 },
            { title: t('Unit Price'), dataIndex: 'unitPrice', key: 'unitPrice', align: 'right' as const, width: 120, render: (v: number) => `$${v?.toLocaleString() || 0}` },
            { title: t('Discount'), dataIndex: 'discount', key: 'discount', align: 'right' as const, width: 100, render: (v: number, r: any) => r.discountType === 'percentage' ? `${v}%` : `$${v}` },
            { title: t('Line Total'), dataIndex: 'lineTotal', key: 'lineTotal', align: 'right' as const, width: 120, render: (v: number) => `$${v?.toLocaleString() || 0}` }
          ]}
          dataSource={selectedQuotation.items}
          pagination={false}
          rowKey="id"
          size="small"
          summary={() => (
            <Table.Summary>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={5} align="right">
                  <strong>{t('Subtotal:')}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  ${selectedQuotation.subtotal?.toLocaleString() || 0}
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={5} align="right">
                  <strong>{t('Discount:')}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  -${selectedQuotation.discountTotal?.toLocaleString() || 0}
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={5} align="right">
                  <strong>{t('Tax:')}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  ${selectedQuotation.taxTotal?.toLocaleString() || 0}
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={5} align="right">
                  <strong>{t('Total:')}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <strong>${selectedQuotation.totalAmount?.toLocaleString() || 0}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />

        {selectedQuotation.options && selectedQuotation.options.length > 0 && (
          <>
            <Divider />
            <Title level={5}>{t('Options')}</Title>
            <Table
              columns={[
                { title: t('Item Title'), dataIndex: 'itemCode', key: 'itemCode', width: 120 },
                { title: t('Subcategory'), dataIndex: 'subcategory', key: 'subcategory', width: 150 },
                { title: t('Description'), dataIndex: 'description', key: 'description' },
                { title: t('Qty'), dataIndex: 'quantity', key: 'quantity', align: 'right' as const, width: 80 },
                { title: t('Unit'), dataIndex: 'unit', key: 'unit', width: 100 },
                { title: t('Unit Price'), dataIndex: 'unitPrice', key: 'unitPrice', align: 'right' as const, width: 120, render: (v: number) => `$${v?.toLocaleString() || 0}` },
                { title: t('Discount'), dataIndex: 'discount', key: 'discount', align: 'right' as const, width: 100, render: (v: number, r: any) => r.discountType === 'percentage' ? `${v}%` : `$${v}` },
                { title: t('Line Total'), dataIndex: 'lineTotal', key: 'lineTotal', align: 'right' as const, width: 120, render: (v: number) => `$${v?.toLocaleString() || 0}` }
              ]}
              dataSource={selectedQuotation.options}
              pagination={false}
              rowKey="id"
              size="small"
            />
          </>
        )}

        {(selectedQuotation.notes || selectedQuotation.terms || (selectedQuotation as any).paymentTermsText) && (
          <>
            <Divider />
            <Row gutter={16}>
              {selectedQuotation.notes && (
                <Col span={12}>
                  <Title level={5}>{t('Notes')}</Title>
                  <Text>{selectedQuotation.notes}</Text>
                </Col>
              )}
              {selectedQuotation.terms && (
                <Col span={12}>
                  <Title level={5}>{t('Terms & Conditions')}</Title>
                  <Text>{selectedQuotation.terms}</Text>
                </Col>
              )}
            </Row>
            {(selectedQuotation as any).paymentTermsText && (
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={24}>
                  <Title level={5}>{t('Payment Terms')}</Title>
                  <Text>{(selectedQuotation as any).paymentTermsText}</Text>
                </Col>
              </Row>
            )}
          </>
        )}

        {(selectedQuotation.salesOrder || selectedQuotation.invoice) && (
          <>
            <Divider />
            <Title level={5}>{t('Linked Documents')}</Title>
            <Space>
              {selectedQuotation.salesOrder && (
                <Tag color="blue" icon={<ShoppingCartOutlined />}>
                  {t('Sales Order:')} {selectedQuotation.salesOrder.orderNo}
                </Tag>
              )}
              {selectedQuotation.invoice && (
                <Tag color="green" icon={<DollarOutlined />}>
                  {t('Invoice:')} {selectedQuotation.invoice.invoiceNo}
                </Tag>
              )}
            </Space>
          </>
        )}
      </Modal>
    );
  };

  const renderQuotationsTab = () => (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={4}>
          <Card>
            <Statistic
              title={t('Total Quotations')}
              value={stats.totalQuotations}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title={t('Draft')}
              value={stats.draftQuotations}
              valueStyle={{ color: '#8c8c8c' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title={t('Sent')}
              value={stats.sentQuotations}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title={t('Accepted')}
              value={stats.acceptedQuotations}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title={t('Total Amount')}
              value={stats.totalAmount}
              prefix="$"
              precision={2}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title={t('Conversion Rate')}
              value={stats.conversionRate}
              suffix="%"
              precision={1}
              valueStyle={{ color: stats.conversionRate >= 50 ? '#52c41a' : '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={t('Quotations')}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            {t('New Quotation')}
                          </Button>
        }
      >
        {/*
          Search plus the three filters from #19, on one wrapping row so they
          stay usable on a narrow screen. Everything filters the list already in
          the browser — it is fetched whole (limit 1000) — so results are
          instant. The KPI cards above come from a separate stats call and
          deliberately keep showing the company totals, not the filtered subset.
        */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16, alignItems: 'center' }}>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder={t('Search by quotation no., customer or salesperson')}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 320 }}
          />
          <Select
            mode="multiple"
            placeholder={t('Status')}
            style={{ minWidth: 190 }}
            allowClear
            maxTagCount="responsive"
            value={statusFilter}
            onChange={setStatusFilter}
          >
            <Option value="DRAFT">{t('Draft')}</Option>
            <Option value="SENT">{t('Sent')}</Option>
            <Option value="ACCEPTED">{t('Accepted')}</Option>
            <Option value="REJECTED">{t('Rejected')}</Option>
            <Option value="EXPIRED">{t('Expired')}</Option>
            <Option value="CANCELLED">{t('Cancelled')}</Option>
          </Select>
          <Select
            mode="multiple"
            placeholder={t('Salesperson')}
            style={{ minWidth: 190 }}
            allowClear
            showSearch
            maxTagCount="responsive"
            value={salespersonFilter}
            onChange={setSalespersonFilter}
          >
            {salespeopleInList.names.map(n => (
              <Option key={n} value={n}>{n}</Option>
            ))}
            {salespeopleInList.anyBlank && (
              <Option key={NO_SALESPERSON} value={NO_SALESPERSON}>{t('(No salesperson)')}</Option>
            )}
          </Select>
          <RangePicker
            placeholder={[t('Date from'), t('Date to')]}
            value={dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null}
            onChange={(dates) =>
              setDateRange(
                dates && dates[0] && dates[1]
                  ? [dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')]
                  : null
              )
            }
          />
          {(statusFilter.length > 0 || salespersonFilter.length > 0 || dateRange || searchText) && (
            <Button
              type="link"
              onClick={() => {
                setStatusFilter([]); setSalespersonFilter([]); setDateRange(null); setSearchText('');
              }}
            >
              {t('Clear filters')}
            </Button>
          )}
          <span style={{ color: '#888', marginLeft: 'auto' }}>
            {filteredQuotations.length === quotations.length
              ? t('{{n}} quotations', { n: quotations.length })
              : t('{{n}} of {{total}} quotations', { n: filteredQuotations.length, total: quotations.length })}
          </span>
        </div>
        <Table
          columns={columns}
          dataSource={filteredQuotations}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} quotations`
          }}
        />
      </Card>
    </>
  );

  const renderTemplatesTab = () => (
    <QuotationItemTemplateList />
  );

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>{t('Quotation Management')}</Title>
        <Text type="secondary">
          {t('Create, manage, and track customer quotations')}
                          </Text>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'quotations',
            label: (
              <span>
                <FileTextOutlined />
                {t('Quotations')}
                                  </span>
            ),
            children: renderQuotationsTab()
          },
          {
            key: 'templates',
            label: (
              <span>
                <AppstoreOutlined />
                {t('Templates')}
                                  </span>
            ),
            children: renderTemplatesTab()
          }
        ]}
      />

      {renderQuotationModal()}
      {renderDetailModal()}
    </div>
  );
};

export default QuotationManagement;